import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { auth } from '../firebase';
import { Logo } from '../components/Logo';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode') ?? '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [codeValid, setCodeValid] = useState<boolean | null>(null);

  useEffect(() => {
    if (!oobCode) {
      setCodeValid(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((e) => { setEmail(e); setCodeValid(true); })
      .catch(() => setCodeValid(false));
  }, [oobCode]);

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return 'Password must be at least 8 characters.';
    if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter.';
    if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must contain a number.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (err) {
      const code = (err as { code?: string }).code ?? '';
      if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        setError('Password must be at least 8 characters and include uppercase, lowercase, and a number.');
      } else if (code === 'auth/expired-action-code') {
        setError('This link has expired. Request a new one from the login page.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex w-full bg-white dark:bg-bg-deep text-gray-900 dark:text-white">
      <div className="w-full flex items-center justify-center p-8">
        <div className="w-full max-w-md">

          <div className="flex justify-center mb-8">
            <Logo className="h-10 w-auto text-gray-900 dark:text-white" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold font-display text-gray-900 dark:text-white">
              Set new password
            </h1>
            {email && (
              <p className="text-gray-500 dark:text-gray-400 text-meta mt-1">
                for <span className="text-gray-700 dark:text-gray-300">{email}</span>
              </p>
            )}
          </div>

          {codeValid === null && (
            <p className="text-center text-meta text-gray-400">Verifying link…</p>
          )}

          {codeValid === false && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-xl text-meta text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                This reset link is invalid or has expired.
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full text-meta text-primary hover:text-primary/80 transition-colors"
              >
                Back to sign in
              </button>
            </div>
          )}

          {codeValid === true && !done && (
            <>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-xl mb-4 text-meta text-red-600 dark:text-red-400"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 8 chars, upper, lower, number"
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full font-medium text-meta hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving…' : 'Set Password'}
                </motion.button>
              </form>
            </>
          )}

          {done && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-xl text-meta text-green-700 dark:text-green-400">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                Password updated. You can now sign in.
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/login')}
                className="w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full font-medium text-meta hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors"
              >
                Go to sign in
              </motion.button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
